import { Trans, useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Skeleton,
  SkeletonLine,
  SkeletonRegion,
} from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { cn } from "@jbnl/shared-ui";
import { Ontvangers } from "./Ontvangers";
import { momentLang, vensterStatus } from "./venster";
import {
  MAIL_STATUS,
  doelgroepLabel,
  mailStatusLabel,
  useAankondigen,
  useAankondiging,
  useAankondigingIntrekken,
  type Aankondiging as AankondigingDto,
  type Inschrijfperiode,
} from "./useInschrijfperiodes";

/**
 * §3.3, never automatic: the trigger is "the data is ready", which only a human knows (ADR-0014).
 * Its own card rather than a toolbar button, because it sends two hundred mails.
 */
export function Aankondiging({
  seizoen,
  periode,
}: {
  seizoen: { id: string };
  periode: Inschrijfperiode;
}) {
  const { t } = useTranslation("inschrijfperiodes");
  const { data, isPending, error } = useAankondiging(periode.id);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("aankondiging.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isPending && (
          <SkeletonRegion className="space-y-3">
            <SkeletonLine className="w-full" />
            <SkeletonLine className="w-4/5" />
            <Skeleton className="h-9 w-36" />
          </SkeletonRegion>
        )}
        {error && <p className="text-sm text-destructive">{error.message}</p>}
        {data && (
          <Inhoud seizoen={seizoen} periode={periode} aankondiging={data} />
        )}
      </CardContent>
    </Card>
  );
}

function Inhoud({
  seizoen,
  periode,
  aankondiging,
}: {
  seizoen: { id: string };
  periode: Inschrijfperiode;
  aankondiging: AankondigingDto;
}) {
  const aankondigen = useAankondigen(seizoen.id, periode.id);
  const intrekken = useAankondigingIntrekken(seizoen.id, periode.id);

  const doelgroep = doelgroepLabel(periode.targetGroup).toLowerCase();
  const aantal = Number(aankondiging.recipientCount);
  const bezig = aankondigen.isPending || intrekken.isPending;

  // Refused by the API too. Here so the beheerder learns it before the click rather than from a
  // red sentence after it, which is the difference between a rule and a rejection.
  const { gesloten } = vensterStatus(periode);

  if (!aankondiging.announcedAt) {
    return (
      <NogNietAangekondigd
        periode={periode}
        aantal={aantal}
        doelgroep={doelgroep}
        gesloten={gesloten}
        aankondigen={aankondigen}
      />
    );
  }

  return (
    <Aangekondigd
      seizoen={seizoen}
      periode={periode}
      aankondiging={aankondiging}
      announcedAt={aankondiging.announcedAt}
      aantal={aantal}
      doelgroep={doelgroep}
      gesloten={gesloten}
      bezig={bezig}
      aankondigen={aankondigen}
      intrekken={intrekken}
    />
  );
}

function NogNietAangekondigd({
  periode,
  aantal,
  doelgroep,
  gesloten,
  aankondigen,
}: {
  periode: Inschrijfperiode;
  aantal: number;
  doelgroep: string;
  gesloten: boolean;
  aankondigen: ReturnType<typeof useAankondigen>;
}) {
  const { t } = useTranslation(["inschrijfperiodes", "common"]);

  return (
    <>
      <p className="text-sm text-muted-foreground">
        {t("aankondiging.nogNiet")}
      </p>

      {/* Resolved server-side at this moment, so it is also the cheapest check that the Portaal
        sync worked: two hundred is a mirror, four is a sync that failed quietly. */}
      <p className="text-sm">
        <Trans
          t={t}
          i18nKey="aankondiging.bereik"
          count={aantal}
          values={{ doelgroep }}
          components={{ strong: <strong /> }}
        />
      </p>

      {gesloten && <Alert tone="warning">{t("aankondiging.gesloten")}</Alert>}

      {/* Radix's Root draws nothing, so this row lays out the trigger and only the trigger. */}
      <div className="flex justify-end">
        <AankondigenDialoog
          periode={periode}
          aantal={aantal}
          doelgroep={doelgroep}
          gesloten={gesloten}
          aankondigen={aankondigen}
        />
      </div>

      {aantal === 0 && (
        <p className="text-sm text-destructive">
          {t("aankondiging.geenOntvangers")}
        </p>
      )}
      {aankondigen.error && (
        <p className="text-sm text-destructive">{aankondigen.error.message}</p>
      )}
    </>
  );
}

function AankondigenDialoog({
  periode,
  aantal,
  doelgroep,
  gesloten,
  aankondigen,
}: {
  periode: Inschrijfperiode;
  aantal: number;
  doelgroep: string;
  gesloten: boolean;
  aankondigen: ReturnType<typeof useAankondigen>;
}) {
  const { t } = useTranslation(["inschrijfperiodes", "common"]);

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button disabled={gesloten || aankondigen.isPending || aantal === 0}>
          {aankondigen.isPending
            ? t("common:actions.busy")
            : t("aankondiging.aankondigen")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("aankondiging.bevestigTitle", { count: aantal })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("aankondiging.bevestigDescription", {
              doelgroep,
              periode: periode.name,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common:actions.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(buttonVariants())}
            onClick={() => aankondigen.mutate()}
          >
            {t("aankondiging.bevestigActie", { count: aantal })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Aangekondigd({
  seizoen,
  periode,
  aankondiging,
  announcedAt,
  aantal,
  doelgroep,
  gesloten,
  bezig,
  aankondigen,
  intrekken,
}: {
  seizoen: { id: string };
  periode: Inschrijfperiode;
  aankondiging: AankondigingDto;
  announcedAt: string;
  aantal: number;
  doelgroep: string;
  gesloten: boolean;
  bezig: boolean;
  aankondigen: ReturnType<typeof useAankondigen>;
  intrekken: ReturnType<typeof useAankondigingIntrekken>;
}) {
  const { t } = useTranslation(["inschrijfperiodes", "common"]);

  return (
    <>
      <p className="text-sm">
        <Trans
          t={t}
          i18nKey="aankondiging.aangekondigdOp"
          values={{ moment: momentLang(announcedAt) }}
          components={{ strong: <strong /> }}
        />
      </p>

      <Tellingen counts={aankondiging.counts} />

      {aankondiging.problems.length > 0 && (
        <Problemen problems={aankondiging.problems} />
      )}

      <p className="text-sm text-muted-foreground">
        {t("aankondiging.nuBereik", { count: aantal, doelgroep })}
      </p>

      {/* Named for what it does: a sent mail is gone, and the stamp only stops a second send, so
        removing it re-arms. The API keeps the two acts separate; the screen composes them. */}
      <Ontvangers seizoen={seizoen} periode={periode} gesloten={gesloten} />

      <div className="flex justify-end">
        <OpnieuwAankondigenDialoog
          aantal={aantal}
          doelgroep={doelgroep}
          gesloten={gesloten}
          bezig={bezig}
          announcedAt={announcedAt}
          aankondigen={aankondigen}
          intrekken={intrekken}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {t("aankondiging.onherroepelijk")}
      </p>

      {intrekken.error && (
        <p className="text-sm text-destructive">{intrekken.error.message}</p>
      )}
      {aankondigen.error && (
        <p className="text-sm text-destructive">{aankondigen.error.message}</p>
      )}
    </>
  );
}

function OpnieuwAankondigenDialoog({
  aantal,
  doelgroep,
  gesloten,
  bezig,
  announcedAt,
  aankondigen,
  intrekken,
}: {
  aantal: number;
  doelgroep: string;
  gesloten: boolean;
  bezig: boolean;
  announcedAt: string;
  aankondigen: ReturnType<typeof useAankondigen>;
  intrekken: ReturnType<typeof useAankondigingIntrekken>;
}) {
  const { t } = useTranslation(["inschrijfperiodes", "common"]);

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="destructive"
          disabled={gesloten || bezig || aantal === 0}
        >
          {bezig ? t("common:actions.busy") : t("aankondiging.opnieuw")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogTitle>
          {t("aankondiging.opnieuwTitle", { count: aantal })}
        </AlertDialogTitle>
        <AlertDialogDescription>
          <Trans
            t={t}
            i18nKey="aankondiging.opnieuwDescription"
            values={{ moment: momentLang(announcedAt), doelgroep }}
            components={{ strong: <strong /> }}
          />
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common:actions.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(buttonVariants({ variant: "destructive" }))}
            onClick={() => {
              // Clear the stamp, then send. Two calls rather than one because the stamp is
              // audited in its own right - a beheerder re-announcing is a thing the trail has
              // to be able to show. If the send fails after the stamp is cleared the card
              // falls back to "nog niet aangekondigd" with its own button, which is a state a
              // beheerder can see and act on rather than a half-done act they cannot.
              intrekken.mutate(undefined, {
                onSuccess: () => aankondigen.mutate(),
              });
            }}
          >
            {t("aankondiging.bevestigActie", { count: aantal })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Ordered by the life of a mail rather than by the enum, so the row reads the way mails move. */
function Tellingen({ counts }: { counts: AankondigingDto["counts"] }) {
  const { t } = useTranslation("inschrijfperiodes");

  const volgorde = [
    MAIL_STATUS.queued,
    MAIL_STATUS.sent,
    MAIL_STATUS.delivered,
    MAIL_STATUS.failed,
    MAIL_STATUS.bounced,
    MAIL_STATUS.complained,
  ];

  const zichtbaar = volgorde
    .map((status) => ({
      status,
      aantal: Number(
        counts.find((telling) => telling.status === status)?.count ?? 0,
      ),
    }))
    .filter((telling) => telling.aantal > 0);

  if (zichtbaar.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("aankondiging.geenMails")}
      </p>
    );
  }

  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
      {zichtbaar.map(({ status, aantal }) => (
        <div key={status}>
          <dt className="text-xs text-muted-foreground">
            {mailStatusLabel(status)}
          </dt>
          <dd className="font-medium">{aantal}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Why the delivery events are consumed at all: a hard bounce means a contactpersoon has left,
 * and the Portaal fix needs to know whose address it was.
 */
function Problemen({ problems }: { problems: AankondigingDto["problems"] }) {
  const { t } = useTranslation("inschrijfperiodes");

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{t("aankondiging.nietAangekomen")}</p>
      <ul className="space-y-1 text-sm">
        {problems.map((probleem) => (
          <li
            key={`${probleem.to}-${probleem.status}`}
            className="text-muted-foreground"
          >
            <span className="font-medium text-foreground">{probleem.to}</span>
            {probleem.reason
              ? t("aankondiging.probleemReden", {
                  status: mailStatusLabel(probleem.status),
                  reden: probleem.reason,
                })
              : t("aankondiging.probleem", {
                  status: mailStatusLabel(probleem.status),
                })}
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">
        {t("aankondiging.portaalHint")}
      </p>
    </div>
  );
}
